/**
 * Turns an Azure Storage probe report into the Test Connection rows and the
 * dialog's verdict (spec §4.5 "Outcome model": the exhaustive combination
 * table over the ENABLED services).
 *
 * Pure so the outcome table is unit-tested without the dialog: the same report
 * drives Test (here) and Connect (main process), and they must agree.
 */
import type {
  StorageErrorCategory,
  StorageProbeOutcome,
  StorageProbeReport,
  StorageProbeRung,
  StorageService,
} from '@/types/azureStorage';
import type { TestLeg } from '../components/TestConnectionStepper';
import { failedLegLead } from './storageLegGuidance';

/** Row order of the stepper — the spec's "Blob · Table · Queue · File" sketch. */
const SERVICE_ORDER: readonly StorageService[] = ['blob', 'table', 'queue', 'file'];

const SERVICE_LABELS: Record<StorageService, string> = {
  blob: 'Blob endpoint',
  table: 'Table endpoint',
  queue: 'Queue endpoint',
  file: 'File endpoint',
};

const RUNG_LABELS: Record<StorageProbeRung, string> = {
  list: 'listed',
  'service-properties': 'read service properties',
  'object-properties': 'read object properties',
  'query-entities': 'queried entities',
  'queue-metadata': 'read queue metadata',
  'peek-messages': 'peeked messages',
};

/** The File leg's remedy link when Entra needs share names (focuses the named-shares form field). */
export const ADD_SHARE_NAMES = { id: 'add-share-names', label: 'Add share names' } as const;

/** Which failed error is worth showing when nothing succeeded (spec §4.5). */
const CATEGORY_PRIORITY = ['authentication', 'authorization', 'network', 'timeout'] as const;

/**
 * A failed service's STRUCTURED error, kept beside the rows (C2 review C2-02 / C2-05): the connection dialog decides from
 * it whether Save anyway applies (a refusal of the settings — `invalid_input`, `unsupported` — is not an outage) and
 * which field the failure is about. Never the message.
 */
export type StorageFailureContext = { service?: StorageService; category: StorageErrorCategory; code?: string; fixKind?: string };

export type StorageTestOutcome = {
  legs: TestLeg[];
  /** One line over the rows (C2 N1): "Blob verified · Table and Queue refused" — the essentials even in a short region. */
  summary: string;
  /** ≥ 1 enabled service verified. */
  success: boolean;
  /** The failure sentence; empty on success. */
  message: string;
  /** Analytics bucket for `feature:storage_connect_probe`. */
  services: 'all' | 'partial' | 'none';
  /** Services whose probe FAILED — unticked in the form; skipped ones stay ticked. */
  failed: StorageService[];
  /** Services that listed but whose data read was refused (RBAC fix 2026-09-25) — the connection still saves. */
  dataDenied: StorageService[];
  /** Each failed service's structured error, in row order. */
  failures: StorageFailureContext[];
};

/**
 * An `ok` listing carries the data rung's verdict (RBAC fix 2026-09-25): "Verified" only when the named item's data
 * was read; a refused read, a role on some items only (`partial`) or an unanswered check is amber; an empty account
 * is neutral "data access not checked" — never a green "Verified" (review 2026-09-25 MINOR 7).
 */
function legDetail(outcome: StorageProbeOutcome): string {
  if (outcome.state === 'ok') {
    const data = outcome.dataAccess;
    if (!data) return `Verified — ${RUNG_LABELS[outcome.rung]}`;
    // Every verdict but `verified` is a whole sentence ("Listed, but …", "Listed · data access not checked …").
    return data.state === 'verified' ? `Verified — ${data.detail}` : data.detail;
  }
  if (outcome.state === 'failed') return outcome.error.message;
  // "No Blob host was found for this account: …" (`storageServiceNotOfferedReason`) — not a "not verified".
  if (notOffered(outcome)) return outcome.detail;
  return `not verified · ${outcome.detail}`;
}

const SERVICE_NAMES: Record<StorageService, string> = { blob: 'Blob', table: 'Table', queue: 'Queue', file: 'File' };

/**
 * C2 N19 + Codex C2-02: a refused leg leads with what it means and what to do, in words that fit the dialog the user is
 * already in; the service's own sentence moves under Details. The lead comes ONLY from main's structured `probe`
 * context (`storageLegGuidance.ts`) — never from main's prose or from an absent `fix.dataRole`. Without a requirement
 * (network, not found, timeout …) the leg keeps its sentence as the lead, with no Details.
 */
function failedLeg(service: StorageService, outcome: Extract<StorageProbeOutcome, { state: 'failed' }>, reportScope: StorageProbeReport['scope']): Pick<TestLeg, 'detail' | 'details'> {
  const lead = outcome.probe ? failedLegLead(service, outcome.probe, reportScope) : undefined;
  return lead ? { detail: lead, details: outcome.error.message } : { detail: outcome.error.message };
}

const joinNames = (names: string[]): string =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/** "Blob verified · Table and Queue refused" (the order of the rows; empty groups left out). */
function summaryLine(entries: Array<[StorageService, StorageProbeOutcome]>, legs: TestLeg[]): string {
  const group = (statuses: TestLeg['status'][]) => entries.flatMap(([service], index) => (statuses.includes(legs[index].status) ? [SERVICE_NAMES[service]] : []));
  const parts: string[] = [];
  const verified = group(['ok']);
  const limited = group(['warning', 'skipped']);
  const refused = group(['error']);
  const absent = entries.flatMap(([service, outcome]) => (notOffered(outcome) ? [SERVICE_NAMES[service]] : []));
  if (verified.length) parts.push(`${joinNames(verified)} verified`);
  if (limited.length) parts.push(`${joinNames(limited)} not fully verified`);
  if (refused.length) parts.push(`${joinNames(refused)} refused`);
  // Both causes in the summary too (review round 3): the account type has no such service, or this computer's DNS.
  if (absent.length) parts.push(`${joinNames(absent)} ${absent.length === 1 ? 'host' : 'hosts'} not found (account type or DNS)`);
  return parts.join(' · ');
}

/** A service whose default host was not found while another answered (`electron/azure-storage/serviceNotOffered.ts`): neither a check nor a failure. */
const notOffered = (outcome: StorageProbeOutcome): boolean => outcome.state === 'skipped' && outcome.reason === 'service-not-offered';

function legStatus(outcome: StorageProbeOutcome): TestLeg['status'] {
  if (notOffered(outcome)) return 'not-attempted';
  if (outcome.state === 'ok') {
    const data = outcome.dataAccess?.state;
    return data === undefined || data === 'verified' ? 'ok' : data === 'nothing-to-check' ? 'skipped' : 'warning';
  }
  return outcome.state === 'failed' ? 'error' : 'skipped';
}

function failureMessage(entries: Array<[StorageService, StorageProbeOutcome]>): string {
  const failures = entries.flatMap(([, outcome]) => (outcome.state === 'failed' ? [outcome.error] : []));
  if (failures.length > 0) {
    const ranked = [...failures].sort(
      (a, b) => categoryRank(a.category) - categoryRank(b.category)
    );
    return ranked[0].message;
  }
  const details = entries.flatMap(([service, outcome]) =>
    outcome.state === 'skipped' ? [`${SERVICE_LABELS[service]}: ${outcome.detail}`] : []
  );
  return `No enabled service could be verified — ${details.join('; ')}`;
}

function categoryRank(category: string): number {
  const index = CATEGORY_PRIORITY.indexOf(category as (typeof CATEGORY_PRIORITY)[number]);
  return index === -1 ? CATEGORY_PRIORITY.length : index;
}

export function storageTestOutcome(report: StorageProbeReport): StorageTestOutcome {
  const entries = SERVICE_ORDER.flatMap((service) => {
    const outcome = report.services[service];
    return outcome ? [[service, outcome] as [StorageService, StorageProbeOutcome]] : [];
  });

  const legs: TestLeg[] = entries.map(([service, outcome]) => ({
    id: service,
    label: SERVICE_LABELS[service],
    status: legStatus(outcome),
    ...(outcome.state === 'failed' ? failedLeg(service, outcome, report.scope) : { detail: legDetail(outcome) }),
    // The service's own answer behind a not-offered leg (the DNS error) stays under Details.
    ...(outcome.state === 'skipped' && outcome.details ? { details: outcome.details } : {}),
    // S7 §3.2: Entra could not list shares and no names were given — the remedy is the named-shares form field.
    ...(outcome.state === 'skipped' && outcome.reason === 'share-names-required' ? { action: ADD_SHARE_NAMES } : {}),
  }));

  const okCount = entries.filter(([, outcome]) => outcome.state === 'ok').length;
  const success = okCount > 0;
  const services = okCount === 0 ? 'none' : okCount === entries.length ? 'all' : 'partial';

  return {
    legs,
    summary: summaryLine(entries, legs),
    success,
    message: success ? '' : failureMessage(entries),
    services,
    failed: entries.flatMap(([service, outcome]) => (outcome.state === 'failed' ? [service] : [])),
    dataDenied: entries.flatMap(([service, outcome]) => (outcome.state === 'ok' && outcome.dataAccess?.state === 'denied' && !outcome.dataAccess.unchecked ? [service] : [])),
    failures: entries.flatMap(([service, outcome]) => (outcome.state === 'failed'
      ? [{ service, category: outcome.error.category, ...(outcome.error.code ? { code: outcome.error.code } : {}), ...(outcome.error.fix ? { fixKind: outcome.error.fix.kind } : {}) }]
      : [])),
  };
}
